import { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Stack,
  TableCell,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import MUITableHead from '@mui/material/TableHead';
import { size } from 'lodash';

import { TableColumnType } from '../@types';

import { TableHeadProps } from './props';

export const TableHead = <T,>(props: TableHeadProps<T>) => {
  const {
    checked = 'none',
    defaultOrderDirection = 'asc',
    defaultOrderBy,
    columns = [],
    tableRowProps,
    enableDivider,
    enableDividerOnLastElement = false,
    onSort,
    onCheckedAll,
    ...restProps
  } = props;

  // ======================= STATE
  const [orderByState, setOrderByState] = useState(defaultOrderBy);
  const [orderDirectionState, setOrderDirectionState] = useState(
    defaultOrderDirection
  );

  // ======================= EFFECTS
  useEffect(() => setOrderByState(defaultOrderBy), [defaultOrderBy]);
  useEffect(
    () => setOrderDirectionState(defaultOrderDirection),
    [defaultOrderDirection]
  );

  // ======================= EVENTS
  const onHandleSortClick = (key: string) => {
    return (e: React.MouseEvent<unknown>) => {
      const isAsc = orderByState === key && orderDirectionState === 'asc';
      const nextDirection = isAsc ? 'desc' : 'asc';
      setOrderByState(key);
      setOrderDirectionState(nextDirection);
      onSort?.(e, key);
    };
  };

  // ======================= RENDER FUNCTIONS
  const renderColumnContent = (column: TableColumnType<T>) => {
    const {
      type = 'cell',
      key,
      title,
      sortable,
      uppercase = true,
      dataIndex,
    } = column;
    const isStringTitle = typeof title === 'string';
    const columnTitle =
      isStringTitle && uppercase ? title.toUpperCase() : title;
    const isCurrent = orderByState === key;

    if (type === 'cell') {
      if (!sortable)
        return (
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {columnTitle}
          </Typography>
        );
      return (
        <TableSortLabel
          active={isCurrent}
          direction={isCurrent ? orderDirectionState : 'asc'}
          onClick={onHandleSortClick(key)}
        >
          {columnTitle}
        </TableSortLabel>
      );
    }

    return (
      <Checkbox
        color="primary"
        indeterminate={checked === 'partial'}
        checked={checked === 'all'}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (!dataIndex) {
            console.error('dataIndex is not provided for checkbox type!');
            return;
          }
          const checkedState = event.target.checked;
          onCheckedAll?.(checkedState, dataIndex);
        }}
        inputProps={{
          'aria-label': 'Select all',
        }}
      />
    );
  };

  // ======================= VIEWS
  if (columns.length === 0) return null;

  return (
    <MUITableHead {...restProps}>
      <TableRow {...tableRowProps}>
        {columns.map((column, i) => {
          const { type = 'cell', key } = column;
          const isCurrent = orderByState === key;
          const lastColumnBorder =
            size(columns) - 1 === i ? 'hidden' : 'visible';
          return (
            <TableCell
              key={key}
              padding={type === 'checkbox' ? 'checkbox' : undefined}
              {...column.headerCellProps}
              sx={{
                width: column.width,
                ...column.headerCellProps?.sx,
                position: 'relative',
              }}
              align={column.align ?? 'left'}
              valign={'middle'}
              sortDirection={isCurrent ? defaultOrderDirection : false}
            >
              <Box
                {...column?.titleContainerProps}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  ...column?.titleContainerProps?.sx,
                }}
              >
                {renderColumnContent(column)}
                {enableDivider && (
                  <Stack
                    sx={{
                      marginRight: -4,
                      px: 2,
                      visibility: enableDividerOnLastElement
                        ? 'visible'
                        : lastColumnBorder,
                    }}
                  >
                    <Box sx={{ background: '#A0A0A0', height: 18, width: 2 }} />
                  </Stack>
                )}
              </Box>
            </TableCell>
          );
        })}
      </TableRow>
    </MUITableHead>
  );
};

// ======================= EXPORT
export * from './props';
export default TableHead;
